import React from 'react';
import i18n from 'i18n';
import { Grid, makeStyles, Typography, Button } from '@material-ui/core';
import {
  VisibilityTwoTone as VisibilityTwoToneIcon,
  VisibilityOffTwoTone as VisibilityOffTwoToneIcon,
  Settings as SettingsIcon,
} from '@material-ui/icons';
import { modalOpen } from 'modules/modal/actions';
import { connect } from 'react-redux';
import { ModalFields } from 'modules/modal/interfaces';
import { Query } from 'hooks/useTableFilters';
import { CardConfig } from './interfaces';
import styles from './styles';
import DashboardCard from './DashboardCard';

const useStyles = makeStyles(styles);

interface DynamicDashboardProps {
  cardsConfig: CardConfig[];
  dashboardModalTitle: string;
  defaultSelectedCards: string[];
  filters: Record<string, string | string[]>;
  isLoading: boolean;
  storageKey: string;
  title: string;
  getDashboardConfiguration: ({
    selectedCards,
    setSelectedCards,
  }) => React.ReactNode;
  modalOpen: (props: ModalFields) => void;
  onApplyFilter: () => void;
  setQuery: React.Dispatch<React.SetStateAction<Query>>;
}

const DynamicDashboard = ({
  cardsConfig,
  dashboardModalTitle,
  defaultSelectedCards,
  filters,
  isLoading,
  storageKey,
  title,
  getDashboardConfiguration,
  modalOpen,
  onApplyFilter,
  setQuery,
}: DynamicDashboardProps) => {
  const classes = useStyles();

  const getInitialSelectedCards = (): string[] => {
    const savedCards = localStorage.getItem(storageKey);

    return savedCards ? JSON.parse(savedCards) : defaultSelectedCards;
  };

  const [showDashboard, setShowDashboard] = React.useState(true);
  const [selectedCards, setSelectedCards] = React.useState(
    getInitialSelectedCards
  );

  const hasSelectedGraphs = selectedCards.length > 0;

  const getSelectedCardsConfig: CardConfig[] = selectedCards
    .map(cardKey => cardsConfig.find(config => config.key === cardKey))
    .filter((config): config is CardConfig => !!config);

  React.useEffect(() => {
    const selectedCardsConfig = getSelectedCardsConfig;

    selectedCardsConfig.forEach(config => {
      config.getReport();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCards]);

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(selectedCards));
  }, [selectedCards, storageKey]);

  const handleConfigDashboard = () => {
    modalOpen({
      overrideClasses: {
        content: {
          padding: 20,
          maxWidth: 1350,
        },
      },
      title: dashboardModalTitle,
      maxWidth: 'xl',
      content: getDashboardConfiguration({ selectedCards, setSelectedCards }),
    });
  };

  const generateUpdatedFilters = (
    filter: Record<string, string | string[]>
  ) => {
    const updatedFilters = { ...filters };
    const [[key, value]] = Object.entries(filter);

    if (filters) {
      Object.keys(filters).forEach(existingKey => {
        if (existingKey.startsWith(key)) {
          delete updatedFilters[existingKey];
        }
      });
    }

    updatedFilters[key] = value;

    return updatedFilters;
  };

  const generateFilteredFilters = (
    filters: Record<string, string | string[]>
  ) =>
    Object.entries(filters).reduce((acc, [key, value]) => {
      if (value && !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {});

  const updateFilter = (filter: Record<string, string | string[]>) => {
    const updatedFilters = generateUpdatedFilters(filter);

    setQuery(prev => ({
      ...prev,
      filters: generateFilteredFilters(updatedFilters),
    }));
    onApplyFilter();
  };

  return (
    <>
      <Grid container className={classes.dashboardHeader}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            {hasSelectedGraphs && (
              <Typography className={classes.cardsTitle}>
                {`${title}:`}
              </Typography>
            )}
          </Grid>

          <Grid item>
            {hasSelectedGraphs &&
              (!showDashboard ? (
                <Button
                  className={classes.dashboardButtonsHeader}
                  onClick={() => setShowDashboard(!showDashboard)}
                  startIcon={<VisibilityTwoToneIcon />}
                >
                  {i18n.t('EXPAND_DASHBOARD_KEY')}
                </Button>
              ) : (
                <Button
                  className={classes.dashboardButtonsHeader}
                  onClick={() => setShowDashboard(!showDashboard)}
                  startIcon={<VisibilityOffTwoToneIcon />}
                >
                  {i18n.t('HIDE_DASHBOARD_KEY')}
                </Button>
              ))}
            <Button
              className={classes.dashboardButtonsHeaderConfig}
              startIcon={<SettingsIcon />}
              onClick={handleConfigDashboard}
            >
              {i18n.t('CONFIG_DASHBOARD_KEY')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {showDashboard && (
        <Grid
          container
          alignItems="center"
          spacing={2}
          className={classes.cardGrid}
          justify="center"
        >
          {getSelectedCardsConfig.map(
            config =>
              config && (
                <Grid key={config?.key} item xs={4}>
                  <Typography className={classes.cardsTitle}>
                    {i18n.t(config?.title)}
                  </Typography>

                  <DashboardCard
                    cardConfig={config}
                    isLoading={isLoading}
                    filters={filters}
                    modalTitle={i18n.t(config.modalTitle)}
                    filterTooltip={i18n.t(config.tooltip)}
                    updateFilter={updateFilter}
                    infoText={config.infoText}
                  />
                </Grid>
              )
          )}
        </Grid>
      )}
    </>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = {
  modalOpen,
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicDashboard);
